<!-- ViewModule.vue -->
<template>
  <BaseModal
    :is-visible="isVisible"
    :title="module?.name || 'Module Details'"
    subtitle="View module information and content"
    :icon="BookOpen"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
              <div v-if="module" class="space-y-6">
                <!-- Module Information Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Module Details -->
                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Module Title
                      </label>
                      <div class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center">
                        {{ module.name }}
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Description
                      </label>
                      <div class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[80px] flex items-center">
                        {{ module.description || 'No description available' }}
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <div class="flex items-center">
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                          :class="getStatusClasses(module.status)"
                        >
                          <span class="w-2 h-2 rounded-full mr-2" :class="getStatusDotClass(module.status)"></span>
                          {{ getStatusText(module.status) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- File Information -->
                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Material
                      </label>
                      <div class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center justify-between">
                        <div class="flex items-center min-w-0 flex-1 mr-3">
                          <FileText class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                          <span 
                            class="truncate" 
                            :title="getFileName(module.material_filepath)"
                          >
                            {{ getFileName(module.material_filepath) }}
                          </span>
                        </div>
                        <div class="flex items-center space-x-2 flex-shrink-0">
                          <button
                            v-if="module.material_filepath && isAdmin"
                            class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            @click="downloadFile"
                            :disabled="isDownloading"
                            title="Download file (Admin only)"
                          >
                            <Download v-if="!isDownloading" class="h-4 w-4" />
                            <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          </button>
                          <div
                            v-else-if="module.material_filepath && !isAdmin"
                            class="flex items-center text-amber-600 dark:text-amber-400"
                            title="Download restricted to administrators"
                          >
                            <ShieldAlert class="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                      <!-- Admin-only download notice -->
                      <div v-if="module.material_filepath && !isAdmin" class="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center">
                        <ShieldAlert class="h-4 w-4 mr-1" />
                        Download restricted to administrators
                      </div>
                    </div>

                    <!-- Time Settings -->
                    <div v-if="module.time_setting">
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Schedule
                      </label>
                      <div class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center">
                        <div class="flex items-center text-sm">
                          <Calendar class="h-4 w-4 text-blue-500 mr-2" />
                          <span>{{ formatDateRange(module.time_setting.start_date, module.time_setting.end_date) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Created Date (moved to file preview info) - original block removed -->
                  </div>
                </div>

                <!-- File Preview Section -->
                <div v-if="module.material_filepath" class="border-t pt-6">
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    File Preview
                  </label>
                  
                  <!-- Show preview if loaded -->
                  <div v-if="showPreview && filePreview" class="space-y-4">
                    <!-- File info -->
                    <div class="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                      <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span class="font-medium text-gray-700 dark:text-gray-300">File:</span>
                          <span 
                            class="ml-2 text-gray-600 dark:text-gray-400"
                            :title="filePreview.filename.length > truncationLimit ? filePreview.filename : undefined"
                          >
                            {{ truncateFilename(filePreview.filename) }}
                          </span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700 dark:text-gray-300">Size:</span>
                          <span class="ml-2 text-gray-600 dark:text-gray-400">{{ filePreview.file_size_formatted }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                          <span class="ml-2 text-gray-600 dark:text-gray-400">{{ filePreview.file_extension.toUpperCase() }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700 dark:text-gray-300">Created Date:</span>
                          <span class="ml-2 text-gray-600 dark:text-gray-400">{{ formatDate(module.added_at) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Preview content -->
                    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                      <!-- PDF Preview -->
                      <div v-if="filePreview.preview_type === 'pdf'" class="w-full">
                        <iframe
                          :src="filePreview.file_url"
                          class="w-full h-96 border-none"
                          title="PDF Preview"
                        ></iframe>
                      </div>
                      
                      <!-- Document Preview (DOCX/DOC) -->
                      <div v-else-if="filePreview.preview_type === 'document'" class="p-6 text-center">
                        <FileText class="h-16 w-16 text-blue-500 mx-auto mb-4" />
                        <p class="text-gray-600 dark:text-gray-400 mb-4">
                          Document preview not supported. Please download to view.
                        </p>
                        <button
                          v-if="isAdmin"
                          class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                          @click="downloadFile"
                          :disabled="isDownloading"
                        >
                          <Download v-if="!isDownloading" class="h-4 w-4 mr-2" />
                          <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Download Document
                        </button>
                        <div v-else class="text-amber-600 dark:text-amber-400 text-sm">
                          Download restricted to administrators
                        </div>
                      </div>
                      
                      <!-- Image Preview -->
                      <div v-else-if="filePreview.preview_type === 'image'" class="p-4">
                        <img
                          :src="filePreview.file_url"
                          :alt="filePreview.filename"
                          class="max-w-full h-auto mx-auto rounded-lg shadow-md"
                        />
                      </div>
                      
                      <!-- Text Preview -->
                      <div v-else-if="filePreview.preview_type === 'text'" class="p-6">
                        <p class="text-gray-600 dark:text-gray-400 mb-4">
                          Text file preview - Please download to view content.
                        </p>
                        <button
                          v-if="isAdmin"
                          class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                          @click="downloadFile"
                          :disabled="isDownloading"
                        >
                          <Download v-if="!isDownloading" class="h-4 w-4 mr-2" />
                          <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Download File
                        </button>
                        <div v-else class="text-amber-600 dark:text-amber-400 text-sm">
                          Download restricted to administrators
                        </div>
                      </div>
                      
                      <!-- Unsupported file type -->
                      <div v-else class="p-6 text-center">
                        <FileText class="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <p class="text-gray-500 dark:text-gray-400 mb-4">
                          Preview not supported for this file type
                        </p>
                        <button
                          v-if="isAdmin"
                          class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                          @click="downloadFile"
                          :disabled="isDownloading"
                        >
                          <Download v-if="!isDownloading" class="h-4 w-4 mr-2" />
                          <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Download to View
                        </button>
                        <div v-else class="text-amber-600 dark:text-amber-400 text-sm">
                          Download restricted to administrators
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Preview placeholder -->
                  <div v-else class="bg-gray-50 dark:bg-gray-700 rounded-xl p-8 text-center">
                    <FileText class="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p class="text-gray-500 dark:text-gray-400 mb-4">
                      Click the preview button to view file information
                    </p>
                    <button
                      class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors mr-3"
                      @click="loadFilePreview"
                      :disabled="isLoadingPreview"
                    >
                      <Eye v-if="!isLoadingPreview" class="h-4 w-4 mr-2" />
                      <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                      Preview File
                    </button>
                  </div>
                </div>
              </div>

              <!-- Loading State -->
              <div v-else class="text-center py-8">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p class="text-gray-500 dark:text-gray-400">Loading module details...</p>
              </div>

    <template #footer>
      <!-- Admin notice -->
      <div v-if="module?.material_filepath && !isAdmin" class="flex items-center text-amber-600 dark:text-amber-400 text-sm mr-auto">
        <ShieldAlert class="h-4 w-4 mr-1" />
        Download restricted to administrators
      </div>
      
      <!-- Buttons -->
      <BaseButton
        text="Close"
        variant="secondary"
        @click="closeModal"
      />
      <BaseButton
        v-if="module?.material_filepath && isAdmin"
        text="Download Material"
        variant="primary"
        :loading="isDownloading"
        loading-text="Downloading..."
        :icon="Download"
        @click="downloadFile"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useToastService } from '@/services/toast'
import { useAuthStore } from '@/stores/auth'
import { getErrorMessage } from '@/utils/errorHandler'
import ModulesService from '@/services/modules'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { 
  BookOpen, 
  FileText, 
  Download, 
  Calendar,
  Eye,
  ShieldAlert
} from 'lucide-vue-next'
import type { Module } from '@/services/modules'

interface Props {
  isVisible: boolean
  module: Module | null
}

interface Emits {
  (e: 'close'): void
}

interface FilePreviewData {
  module_id: number
  module_name: string
  filename: string
  file_extension: string
  file_size: number
  file_size_formatted: string
  mime_type: string
  file_url: string
  is_previewable: boolean
  preview_type: string
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const toast = useToastService()
const authStore = useAuthStore()

// Reactive state
const isLoadingPreview = ref(false)
const isDownloading = ref(false)
const filePreview = ref<FilePreviewData | null>(null)
const showPreview = ref(false)
const downloadId = ref<string | null>(null)
const windowWidth = ref(window.innerWidth)

// Computed properties
const isAdmin = computed(() => authStore.isAdmin)

// Computed truncation limit based on screen size
const truncationLimit = computed(() => {
  return windowWidth.value < 760 ? 25 : 50
})

// Helper to truncate filename
const truncateFilename = (filename: string): string => {
  const limit = truncationLimit.value
  if (filename.length > limit) {
    return filename.substring(0, limit - 3) + '...'
  }
  return filename
}

// Watch for module changes to reset preview state
watch(() => props.module, (newModule, oldModule) => {
  // Reset preview state when module changes
  filePreview.value = null
  showPreview.value = false
  
  // Also reset if the module ID changed or material_filepath changed
  if (newModule && oldModule && 
      (newModule.module_id !== oldModule.module_id || 
       newModule.material_filepath !== oldModule.material_filepath)) {
    filePreview.value = null
    showPreview.value = false
  }
}, { immediate: true })

// Helper functions
const getFileName = (filePath: string | null | undefined): string => {
  if (!filePath) return 'No file attached'
  const pathParts = filePath.split('/')
  return pathParts[pathParts.length - 1] || 'Unknown file'
}

const getStatusText = (status: string | number | null | undefined): string => {
  if (status === null || status === undefined) return 'Inactive'
  const statusNum = Number(status)
  return statusNum === 1 ? 'Active' : 'Inactive'
}

const getStatusClasses = (status: string | number | null | undefined): string => {
  const statusNum = Number(status)
  return statusNum === 1 
    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
}

const getStatusDotClass = (status: string | number | null | undefined): string => {
  const statusNum = Number(status)
  return statusNum === 1 ? 'bg-green-500' : 'bg-red-500'
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return 'Invalid date'
  }
}

const formatDateRange = (startDate: string | null | undefined, endDate: string | null | undefined): string => {
  if (!startDate || !endDate) return 'No schedule set'
  
  try {
    const start = new Date(startDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    const end = new Date(endDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    return `${start} - ${end}`
  } catch {
    return 'Invalid date range'
  }
}

const downloadFile = async () => {
  if (!props.module?.material_filepath) {
    toast.error('This module does not have a file attached.')
    return
  }

  if (!isAdmin.value) {
    toast.error('Only administrators can download module materials.')
    return
  }

  // Prevent multiple simultaneous downloads
  if (isDownloading.value) {
    toast.warning('Please wait for the current download to complete.')
    return
  }

  isDownloading.value = true
  
  // Generate unique download ID for this session
  const currentDownloadId = `download_${props.module.module_id}_${Date.now()}`
  downloadId.value = currentDownloadId
  
  try {
    const blob = await ModulesService.downloadFile(props.module.module_id)
    
    // Check if this download is still current (user hasn't cancelled)
    if (downloadId.value !== currentDownloadId) {
      return
    }
    
    // Create download link with cache-busting
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Generate filename with timestamp to avoid cache conflicts
    const originalFileName = getFileName(props.module.material_filepath)
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileExtension = originalFileName.split('.').pop()
    const baseName = originalFileName.replace(/\.[^/.]+$/, '')
    const fileName = `${baseName}_${timestamp}.${fileExtension}`
    
    link.download = fileName
    
    // Add cache-busting attributes
    link.setAttribute('download', fileName)
    link.style.display = 'none'
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    
    // Cleanup with slight delay to ensure download starts
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link)
      }
      window.URL.revokeObjectURL(url)
    }, 100)
  } catch (error: any) {
    console.error('Download failed:', error)
    toast.error(getErrorMessage(error, 'Failed to download the file.'))
  } finally {
    isDownloading.value = false
    downloadId.value = null
  }
}

const loadFilePreview = async () => {
  if (!props.module?.material_filepath) {
    return
  }

  isLoadingPreview.value = true
  
  try {
    const response = await ModulesService.previewFile(props.module.module_id)
    if (response.success) {
      filePreview.value = response.data
      showPreview.value = true
    }
  } catch (error: any) {
    console.error('Preview failed:', error)
    toast.error(getErrorMessage(error, 'Failed to load file preview.'))
  } finally {
    isLoadingPreview.value = false
  }
}

// Window resize handler
const handleResize = () => {
  windowWidth.value = window.innerWidth
}

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const closeModal = () => {
  // Cancel any pending downloads
  if (downloadId.value) {
    downloadId.value = null
  }
  
  // Reset state
  isDownloading.value = false
  filePreview.value = null
  showPreview.value = false
  
  emit('close')
}
</script>
